import { makeAutoObservable, runInAction } from 'mobx';
import adminProductService from '../services/adminProductService';

class AdminProductStore {
  products = [];
  queueProducts = [];
  currentProduct = null;
  categories = [];
  loading = false;
  error = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Fetch products awaiting marketing content (queue)
  async fetchProductQueue() {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminProductService.getProductQueue();
      runInAction(() => {
        this.queueProducts = data.results || data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch product queue';
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch all products (with optional filters)
  async fetchProducts(filters = {}) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminProductService.getProducts(filters);
      runInAction(() => {
        this.products = data.results || data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch products';
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch single product by ID
  async fetchProductById(id) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminProductService.getProductById(id);
      runInAction(() => {
        this.currentProduct = data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch product';
        this.loading = false;
      });
      throw error;
    }
  }

  // Enrich product with marketing content
  async enrichProduct(id, enrichData) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminProductService.enrichProduct(id, enrichData);
      runInAction(() => {
        // Update current product
        this.currentProduct = data;

        // Remove from queue if it was there
        this.queueProducts = this.queueProducts.filter(p => p.id !== id);

        // Update in products list if it exists
        const index = this.products.findIndex(p => p.id === id);
        if (index !== -1) {
          this.products[index] = data;
        }

        this.loading = false;
      });
      return data;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to enrich product';
        this.loading = false;
      });
      throw error;
    }
  }

  // Delete product
  async deleteProduct(id) {
    this.loading = true;
    this.error = null;
    try {
      await adminProductService.deleteProduct(id);
      runInAction(() => {
        this.products = this.products.filter(p => p.id !== id);
        this.queueProducts = this.queueProducts.filter(p => p.id !== id);
        if (this.currentProduct?.id === id) {
          this.currentProduct = null;
        }
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to delete product';
        this.loading = false;
      });
      throw error;
    }
  }

  // Toggle product visibility
  async toggleProductVisibility(id, isActive) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminProductService.toggleProductVisibility(id, isActive);
      runInAction(() => {
        // Update in products list
        const index = this.products.findIndex(p => p.id === id);
        if (index !== -1) {
          this.products[index] = { ...this.products[index], is_active: isActive };
        }
        this.loading = false;
      });
      return data;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to toggle product visibility';
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch product categories
  async fetchCategories() {
    try {
      const data = await adminProductService.getCategories();
      runInAction(() => {
        this.categories = data;
      });
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }

  // Clear current product
  clearCurrentProduct() {
    this.currentProduct = null;
  }

  // Clear error
  clearError() {
    this.error = null;
  }
}

export default AdminProductStore;
